<script setup lang="ts">
import { computed } from 'vue';
import { formatCurrency } from '@/helpers/format';

const props = defineProps<{
  estimatedEquityAvailable: number;
  amountOwed: number;
  isReady: boolean;
}>();

// The limit marker's position while the owed amount stays within 120% of the limit.
const FIXED_LIMIT_MARK_PCT = (5 / 6) * 100;

// Beyond this multiple of the limit, the marker starts moving left to stay to scale.
const OVER_SCALE_RATIO = 1.2;

const ratio = computed(() =>
  props.estimatedEquityAvailable > 0 ? props.amountOwed / props.estimatedEquityAvailable : 0,
);

const isOverScale = computed(() => ratio.value > OVER_SCALE_RATIO);

// Once the owed amount exceeds 120% of the limit, the bar represents the owed amount at 100%,
// so the limit marker moves to sit at limit/owed instead of staying fixed.
const limitMarkPct = computed(() => (isOverScale.value ? 100 / ratio.value : FIXED_LIMIT_MARK_PCT));

// Where the colored fill actually ends on the 0-100% track, clamped to the bar's right edge.
const fillEndPct = computed(() => (isOverScale.value ? 100 : Math.min(ratio.value * FIXED_LIMIT_MARK_PCT, 100)));

const greenPct = computed(() => Math.min(fillEndPct.value, limitMarkPct.value));
const redPct = computed(() => Math.max(fillEndPct.value - limitMarkPct.value, 0));

// Keeps the right-aligned owed-amount label from overflowing the left edge when fillEndPct is near 0.
const labelAnchorPct = computed(() => Math.max(fillEndPct.value, 8));
</script>

<template>
  <div class="bg-[#F7FEF9]">
    <h2 class="text-2xl font-medium">Equity snapshot</h2>

    <div class="mt-6">
      <p class="font-medium">Estimated equity available</p>
      <p class="my-2 text-4xl font-bold text-bridgit-green">
        <template v-if="isReady">{{ formatCurrency(estimatedEquityAvailable) }}</template>
        <BxSpinner v-else />
      </p>
      <p class="text-sm text-gray-500">
        This is an estimate based on the property information provided and loan-to-value ratio, and is indicative only.
        Final equity is subject to valuation and credit assessment.
      </p>
    </div>

    <div v-if="isReady" class="relative mt-2 pt-6">
      <span
        class="absolute -top-1 max-w-[45%] truncate text-sm font-semibold text-bridgit-navy"
        :style="{ left: `${labelAnchorPct}%`, transform: 'translateX(-100%)' }"
      >
        {{ formatCurrency(amountOwed) }}
      </span>

      <div class="relative h-6 overflow-hidden rounded-full bg-gray-200">
        <div class="absolute inset-y-0 left-0 bg-bridgit-vibrantGreen" :style="{ width: `${greenPct}%` }" />
        <div
          v-if="redPct > 0"
          class="absolute inset-y-0 rounded-r-full bg-[#FBC8CC]"
          :style="{ left: `${limitMarkPct}%`, width: `${redPct}%` }"
        />
      </div>
      <div class="absolute inset-y-5 h-8 w-0.5 bg-bridgit-navy" :style="{ left: `${limitMarkPct}%` }" />

      <div class="mt-2 flex justify-between text-sm">
        <span
          class="absolute max-w-[45%] truncate font-medium text-gray-500"
          :style="{ left: `${limitMarkPct}%`, transform: 'translateX(-50%)' }"
          >{{ formatCurrency(estimatedEquityAvailable) }}
        </span>
      </div>
    </div>
    <div v-else class="mt-8 flex justify-center pt-6">
      <BxSpinner />
    </div>

    <hr class="mt-6 border-gray-200" />
  </div>
</template>
